import React, { useState, useEffect } from "react";
import { Modal, Text, Button, Input, Checkbox } from "@nextui-org/react";
import { exportAltCustomData } from "../../utils/altEntriesExport";
import { TbFileSpreadsheet, TbBrandAdobe } from "react-icons/tb";

const AltExportModal = ({ visible, onClose, allData = [] }) => {
  const [scope, setScope] = useState("all"); // "all", "uncleared", "cleared", "deductions"
  const [category, setCategory] = useState("all"); // "all", "shop", "customer"
  const [includeDeductions, setIncludeDeductions] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (visible) {
      setScope("all");
      setCategory("all");
      setIncludeDeductions(true);
      setStartDate("");
      setEndDate("");
    }
  }, [visible]);

  const handleExport = (format) => {
    exportAltCustomData(allData, {
      format,
      scope,
      category,
      includeDeductions: scope === "deductions" ? true : includeDeductions,
      startDate,
      endDate,
    });
    onClose();
  };

  const scopeOptions = [
    { key: "all", label: "All" },
    { key: "uncleared", label: "New" },
    { key: "cleared", label: "Cleared" },
    { key: "deductions", label: "Subs" },
  ];

  const categoryOptions = [
    { key: "all", label: "All" },
    { key: "shop", label: "Shop" },
    { key: "customer", label: "Customer" },
  ];

  return (
    <Modal
      closeButton
      aria-labelledby="export-modal-title"
      open={visible}
      onClose={onClose}
      css={{ mw: "460px", p: "14px 18px" }}
    >
      <Modal.Header css={{ pb: "2px" }}>
        <Text id="export-modal-title" b size={16}>
          Data Export
        </Text>
      </Modal.Header>
      <Modal.Body css={{ display: "flex", flexDirection: "column", gap: "12px", py: "8px" }}>
        {/* Scope Row */}
        <div>
          <Text
            b
            size="11px"
            color="$gray600"
            css={{ mb: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            Record Scope
          </Text>
          <div style={{ display: "flex", gap: "6px", width: "100%" }}>
            {scopeOptions.map((opt) => {
              const active = scope === opt.key;
              return (
                <div
                  key={opt.key}
                  onClick={() => setScope(opt.key)}
                  style={{
                    flex: 1,
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "9999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    userSelect: "none",
                    boxSizing: "border-box",
                    border: active ? "2px solid #ff385c" : "2px solid #e9ecef",
                    backgroundColor: active ? "#fff0f2" : "#f8f9fa",
                    color: active ? "#ff385c" : "#495057",
                    transition: "all 0.15s cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Category Row */}
        <div>
          <Text
            b
            size="11px"
            color="$gray600"
            css={{ mb: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            Category
          </Text>
          <div style={{ display: "flex", gap: "6px", width: "100%" }}>
            {categoryOptions.map((opt) => {
              const active = category === opt.key;
              return (
                <div
                  key={opt.key}
                  onClick={() => setCategory(opt.key)}
                  style={{
                    flex: 1,
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "9999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    userSelect: "none",
                    boxSizing: "border-box",
                    border: active ? "2px solid #008489" : "2px solid #e9ecef",
                    backgroundColor: active ? "#e6f3f3" : "#f8f9fa",
                    color: active ? "#008489" : "#495057",
                    transition: "all 0.15s cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Include Deductions Checkbox Container (Fixed Height to prevent modal jumping) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "26px",
            opacity: scope === "deductions" ? 0.35 : 1,
            pointerEvents: scope === "deductions" ? "none" : "auto",
            transition: "opacity 0.15s ease",
          }}
        >
          <Checkbox
            isSelected={includeDeductions}
            onChange={setIncludeDeductions}
            size="sm"
            color="primary"
            isDisabled={scope === "deductions"}
          >
            <Text size="12px" b color={scope === "deductions" ? "$gray500" : "$gray800"}>
              Include Deductions
            </Text>
          </Checkbox>
        </div>

        {/* Date Range Inputs */}
        <div>
          <Text
            b
            size="11px"
            color="$gray600"
            css={{ mb: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            Date Range (Optional)
          </Text>
          <div style={{ display: "flex", gap: "8px" }}>
            <Input
              type="date"
              fullWidth
              size="sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              type="date"
              fullWidth
              size="sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer css={{ justifyContent: "space-between", pt: "8px" }}>
        <Button auto flat color="default" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: "6px" }}>
          <Button
            auto
            size="sm"
            color="success"
            icon={<TbFileSpreadsheet size={16} />}
            onClick={() => handleExport("excel")}
          >
            Excel
          </Button>
          <Button
            auto
            size="sm"
            color="error"
            icon={<TbBrandAdobe size={16} />}
            onClick={() => handleExport("pdf")}
          >
            PDF
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AltExportModal;
